import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../utils/api';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { Plus as PlusIcon } from '@phosphor-icons/react';

interface EmployeeGroup {
  id: string;
  name: string;
}

function CreateNotificationPage() {
  const [loading, setLoading] = useState(false);
  const [employeeGroups, setEmployeeGroups] = useState<EmployeeGroup[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    category: 'system',
    severity: 'info',
    action_url: '',
    action_label: '',
    target_all_users: true,
    target_group_ids: [] as string[],
    visibility: 'general',
    expires_at: '',
  });

  useEffect(() => {
    fetchEmployeeGroups();
  }, []);

  const fetchEmployeeGroups = async () => {
    try {
      const response = await apiFetch('/api/people/employee-groups');
      if (response.ok) {
        const data = await response.json();
        setEmployeeGroups(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch employee groups:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data
      const payload = {
        ...formData,
        target_group_ids: formData.target_all_users ? [] : formData.target_group_ids,
        expires_at: formData.expires_at || null,
      };

      const response = await apiFetch('/api/notifications/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success('Notification created successfully');
        // Reset form
        setFormData({
          title: '',
          message: '',
          category: 'system',
          severity: 'info',
          action_url: '',
          action_label: '',
          target_all_users: true,
          target_group_ids: [],
          visibility: 'general',
          expires_at: '',
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create notification');
      }
    } catch (error) {
      console.error('Failed to create notification:', error);
      toast.error('Failed to create notification');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupSelection = (groupId: string) => {
    setFormData((prev) => ({
      ...prev,
      target_group_ids: prev.target_group_ids.includes(groupId)
        ? prev.target_group_ids.filter((id) => id !== groupId)
        : [...prev.target_group_ids, groupId],
    }));
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Create Notification</h1>
          <p className="text-muted-foreground">
            Send notifications to users about important updates and alerts
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Notification Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Low Inventory Alert"
                  required
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Detailed message about the notification..."
                  rows={4}
                  required
                />
              </div>

              {/* Category and Severity */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger id="category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inventory">📦 Inventory</SelectItem>
                      <SelectItem value="finance">💰 Finance</SelectItem>
                      <SelectItem value="people">👥 People</SelectItem>
                      <SelectItem value="system">⚙️ System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="severity">Severity *</Label>
                  <Select
                    value={formData.severity}
                    onValueChange={(value) => setFormData({ ...formData, severity: value })}
                  >
                    <SelectTrigger id="severity">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">ℹ️ Info</SelectItem>
                      <SelectItem value="warning">⚠️ Warning</SelectItem>
                      <SelectItem value="critical">🚨 Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Action URL and Label */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="action_url">Action URL (optional)</Label>
                  <Input
                    id="action_url"
                    value={formData.action_url}
                    onChange={(e) => setFormData({ ...formData, action_url: e.target.value })}
                    placeholder="/invoices?status=pending"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="action_label">Action Label (optional)</Label>
                  <Input
                    id="action_label"
                    value={formData.action_label}
                    onChange={(e) => setFormData({ ...formData, action_label: e.target.value })}
                    placeholder="View Invoices"
                  />
                </div>
              </div>

              {/* Target All Users */}
              <div className="flex items-center justify-between space-x-2 p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="target_all_users">Send to all users</Label>
                  <p className="text-sm text-muted-foreground">
                    Show this notification to everyone in the organization
                  </p>
                </div>
                <Switch
                  id="target_all_users"
                  checked={formData.target_all_users}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, target_all_users: checked })
                  }
                />
              </div>

              {/* Employee Groups (only if not targeting all users) */}
              {!formData.target_all_users && (
                <div className="space-y-2">
                  <Label>Target Employee Groups *</Label>
                  <div className="border rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                    {employeeGroups.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No employee groups available</p>
                    ) : (
                      employeeGroups.map((group) => (
                        <div key={group.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`group-${group.id}`}
                            checked={formData.target_group_ids.includes(group.id)}
                            onChange={() => toggleGroupSelection(group.id)}
                            className="rounded border-gray-300"
                          />
                          <Label htmlFor={`group-${group.id}`} className="font-normal cursor-pointer">
                            {group.name}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Visibility */}
              <div className="space-y-2">
                <Label htmlFor="visibility">Acknowledgment Behavior *</Label>
                <Select
                  value={formData.visibility}
                  onValueChange={(value) => setFormData({ ...formData, visibility: value })}
                >
                  <SelectTrigger id="visibility">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">
                      General - One acknowledgment dismisses for all
                    </SelectItem>
                    <SelectItem value="personal">
                      Personal - Each user must acknowledge individually
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {formData.visibility === 'general'
                    ? 'When anyone acknowledges, the notification disappears for everyone'
                    : 'Each user must acknowledge the notification individually'}
                </p>
              </div>

              {/* Expiration Date */}
              <div className="space-y-2">
                <Label htmlFor="expires_at">Expiration Date (optional)</Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                />
                <p className="text-sm text-muted-foreground">
                  Notification will automatically hide after this date
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="submit" disabled={loading}>
                  <PlusIcon size={16} weight="duotone" className="mr-2" />
                  {loading ? 'Creating...' : 'Create Notification'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/admin/notifications')({
  component: CreateNotificationPage,
});
